import * as React from 'react';

/**
 * Порог схлопывания. Высота схлопывающейся строки локации на выдаче: как только она ушла
 * за верх экрана, строка переходит в компактный вид. В макете не задан, это рекомендация дизайна.
 */
const SCROLL_COLLAPSE_THRESHOLD_PX = 46;
/**
 * Порог возврата в развёрнутый вид. Отличается от порога схлопывания намеренно: с одним порогом
 * состояние дребезжит. Схлопывание убирает 8px высоты, браузер компенсирует это scroll anchoring'ом
 * и подталкивает позицию скролла обратно под порог — строка разворачивается, возвращает эти 8px,
 * и всё повторяется по кругу. Видно это как моргание: у нас вместе с высотой едут левый и правый
 * края поля, поэтому строка дёргается ещё и по горизонтали. Такой же гистерезис стоит в
 * mobile-search-frontend, где по этой же причине моргала строка фильтров.
 */
const SCROLL_EXPAND_THRESHOLD_PX = 0;

export interface IScrollCollapseOptions {
  /** Класс свёрнутого состояния. */
  scrolledClassName: string;
  /**
   * Вызывается каждый раз, когда скролл переваливает за порог. Нужен, чтобы вызывающий код
   * мог отреагировать на то, что страницу пролистали.
   */
  onCollapse?(): void;
}

/**
 * Следит за позицией скролла: переключает класс свёрнутого состояния и сообщает наружу,
 * что страницу пролистали.
 *
 * Слушает window.scroll с rAF-троттлингом. Сама анимация — CSS transition на классе,
 * здесь только смена состояния.
 */
export function useScrollCollapse(
  ref: React.RefObject<HTMLElement | null>,
  { scrolledClassName, onCollapse }: IScrollCollapseOptions,
): void {
  // Колбэк живёт в ref, чтобы его новая идентичность не пересоздавала подписку на скролл.
  const onCollapseRef = React.useRef(onCollapse);
  onCollapseRef.current = onCollapse;

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const node = ref.current;
    if (!node) {
      return;
    }

    let rafId = 0;
    let isCollapsed = false;

    const apply = (): void => {
      rafId = 0;

      // Гистерезис: свернувшись, строка держится компактной до самого верха страницы.
      const threshold = isCollapsed ? SCROLL_EXPAND_THRESHOLD_PX : SCROLL_COLLAPSE_THRESHOLD_PX;
      const nextCollapsed = window.scrollY > threshold;

      if (nextCollapsed === isCollapsed) {
        return;
      }

      isCollapsed = nextCollapsed;

      node.classList.toggle(scrolledClassName, isCollapsed);

      if (isCollapsed) {
        onCollapseRef.current?.();
      }
    };

    const onScroll = (): void => {
      if (rafId === 0) {
        rafId = window.requestAnimationFrame(apply);
      }
    };

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId !== 0) {
        window.cancelAnimationFrame(rafId);
      }

      node.classList.remove(scrolledClassName);
    };
  }, [ref, scrolledClassName]);
}
